import {
  useActivatePropertyMutation,
  usePartialActivatePropertyMutation
} from "@/src-refactor/modules/store/qrtk/services/realestate/realestate.qrtk";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid } from "@mui/material";
import HTransformObject from "@rf-helper/transform-object.helper";
import MxButton2 from "@rf-l1/buttons/MxButton2";
import MxModalTemplate2 from "@rf-l1/modal/MxModalTemplate2";
import MxFormSubTitleAndDescription from "@rf-l1/page/form/MxFormSubTitleAndDescription";
import MxFormL3NumberField from "@rf-l3/form/MxFormL3NumberField";
import useModal from "@rf/base/modal/useModal";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { IActivatePropertyArgs } from "../util/model";
import {
  ActivatePropertySchema,
  CActivateProperty,
  CActivatePropertyKeys,
  IActivatePropertySchema
} from "../util/propertyDropSchema";

import MxTypography from "@rf-l1/MxTypography";
import MxFormErrorMessage from "@rf-l2/form/MxFormErrorMessage";
import MxFormFieldController from "@rf-l2/form/MxFormFieldController";
import { useMemo } from "react";
import { HToastCatchServerError } from "@rf-helper/cath-error-server";
import MxCardOption from "../../../../../shared/components/refactor/components/MxCardOption";

export interface IDropPropertyTemplateModalData {
  propertyId: string;
}
export const ActivatePropertyTemplateModal = () => {
  const { getData, onConfirm } = useModal();
  const {
    propertyId,
    isProject = false,
    labelSubmitButton,
    hasRestorableUnits = false
  } = getData() as IActivatePropertyArgs;
  const [activateProperty, { isLoading }] = useActivatePropertyMutation();
  const [partialActivateProperty, { isLoading: isLoadingPartialActivateProperty }] =
    usePartialActivatePropertyMutation();

  const form = useForm<IActivatePropertySchema>({
    resolver: yupResolver(ActivatePropertySchema),
    defaultValues: { ...CActivateProperty, isProject }
  });

  const getRequest = (data: IActivatePropertySchema) => {
    const body = HTransformObject(
      {
        days: data.days
      },
      {
        removeUndefined: true,
        removeEmptyArray: true,
        removeNull: true,
        removeEmptyStrings: true,
        removeEmptyObjects: true
      }
    );
    const state = {
      params: {
        id: propertyId
      },
      body
    };

    if (isProject) {
      if (data.actionMode === "full") {
        return activateProperty(state).unwrap();
      }
      return partialActivateProperty(state).unwrap();
    }

    return activateProperty(state).unwrap();
  };

  const onSubmit = (data: IActivatePropertySchema) => {
    const response = getRequest(data);
    toast
      .promise(response, {
        loading: "La propiedad se está activando...",
        success: "La propiedad se ha activado correctamente",
        error: "Ha ocurrido un error al activar la propiedad"
      })
      .then(() => {
        onConfirm();
      })
      .catch(e => HToastCatchServerError(e));
  };

  const actionModeItems = [
    {
      label: "Completo",
      description: "Activa el proyecto y todas sus unidades.",
      value: "full",
      name: "full",
      id: "full",
      show: true
    },
    {
      label: "Parcial",
      description: "Activa el proyecto y restaura unidades al estado anterior.",
      value: "partial",
      name: "partial",
      id: "partial",
      show: hasRestorableUnits
    }
  ].filter(item => item.show);

  const onSelectOptionItem = (value: string) => {
    form.setValue(CActivatePropertyKeys.actionMode, value as "full" | "partial");
  };

  const title = useMemo(() => {
    if (isProject) {
      return `Seleccione una opción para activar el proyecto.`;
    }
    return `Complete las información requerida para ${
      labelSubmitButton?.toLowerCase() || "activar"
    } ${isProject ? "el proyecto" : "la  propiedad"}`;
  }, [isProject]);

  return (
    <MxModalTemplate2>
      <Grid container rowGap={2}>
        <Grid item xs={12} mt={0} mb={0}>
          <MxFormSubTitleAndDescription label={title} description={""} />
        </Grid>

        <Grid item xs={12}>
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <Grid container rowGap={4} columnSpacing={2}>
                {isProject
                  ? actionModeItems.map(item => (
                      <Grid
                        item
                        xs={12}
                        md={actionModeItems.length > 1 ? 6 : 8}
                        mx={actionModeItems.length > 1 ? 0 : "auto"}
                        key={item.value}
                      >
                        <MxFormFieldController
                          withreacthookform={1}
                          name={CActivatePropertyKeys.actionMode}
                        >
                          <MxCardOption
                            selected={
                              form.watch(CActivatePropertyKeys.actionMode) ===
                              item.value
                            }
                            onClick={() => onSelectOptionItem(item.value)}
                            label={item.label}
                            description={item.description}
                          />
                          <MxFormErrorMessage />
                        </MxFormFieldController>
                      </Grid>
                    ))
                  : null}

                <Grid item xs={12}>
                  <MxFormL3NumberField
                    withreacthookform={1}
                    name={CActivatePropertyKeys.days}
                    label="Cantidad de días a extender disponibilidad."
                  />
                </Grid>
                <ButtonsFooter />
              </Grid>
            </form>
          </FormProvider>
        </Grid>
      </Grid>
    </MxModalTemplate2>
  );
};

const ButtonsFooter = () => {
  const { onClose, getData } = useModal();
  const { labelSubmitButton } = getData() as IActivatePropertyArgs;
  return (
    <Grid container item xs={12}>
      <Grid item flex={1} />
      <Grid container item xs="auto" gap={2}>
        <Grid item xs="auto">
          <MxButton2
            testId="cancel-activate-property-button"
            variant="contained"
            color="error"
            onClick={onClose}
          >
            Cancelar
          </MxButton2>
        </Grid>
        <Grid item xs="auto">
          <MxButton2
            testId="activate-property-button"
            variant="contained"
            color="primary"
            type="submit"
          >
            {labelSubmitButton || "Activar"}
          </MxButton2>
        </Grid>
      </Grid>
    </Grid>
  );
};
